import React, {useContext, useMemo, useRef} from "react";
import PropTypes from 'prop-types';
import {TextField} from "@material-ui/core";
import makeStyles from "@material-ui/core/styles/makeStyles";
import {useSelector} from "react-redux";
import MultiLngMenu from "../../Utils/MultiLngMenu";
import formatValue from "../../../../ListUtils/FormatValue";
import {multiLanguageValue, shortcutCombinationMatcher} from "../../../../../Components/JsonScreen/Utils/lib";
import moment from "moment";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import clsx from "clsx";
import IconCustom from "../../../../IconCustom/IconCustom";
import {FieldWrapperContext} from "../../FieldWrapperContext";
import {useEventListener} from "../../../../../Hooks/useEventListener";

const useStyles = makeStyles(() => ({
    textFieldRoot: {
        '& .MuiInput-underline:before': {
            borderBottomColor: 'rgba(0,0,0,0.22)', // Semi-transparent underline
        },
        '& .MuiOutlinedInput-adornedEnd': {
            paddingRight: 0
        }
    },

}));

const SimpleField = (props) => {
    const {
        isMobile, fieldStyles, fieldValue, label, error, fieldRef, autofocus, onChange, field,
        onBlur, type, disabled, required, minLength, maxLength, min, max, decimalPlaces, pattern, multiLanguage,
        autocomplete, fieldPresentation, patternTitle, placeholder, onKeyDown, id, valueDropdownOnly, dropdown,
        endAdornment, startAdornment, screenName, path, disableKeydown, doActions, events
    } = props;
    const userLanguage = useSelector(state => state.app.userLanguage);
    const {openDropdown, closeDropdown, isOpened} = useContext(FieldWrapperContext)
    const classes = useStyles();
    const inputRef = useRef(null)
    const customStyles = (isMobile || fieldStyles.presentation === 'standard') ? {
        width: '100%',
        marginTop: 4
    } : {
        height: 25,
        paddingTop: 3,
        paddingLeft: 2,
        marginBottom: 2
    };
    let inputClassName = fieldStyles.withoutStyles ? 'horizontal-fields-table' : 'form-dynamic-default';
    if (fieldStyles.hideNumberArrows) {
        inputClassName += ' field-hide-arrows'
    }
    if (valueDropdownOnly) {
        inputClassName += ' hide-calendar-picker'
    }
    const inputProps = {
        style: {
            textAlign: fieldStyles.textAlign,
            width: '100%',
            height: fieldStyles.height
        }
    };
    const currentValue = useMemo(() => {
        if (type === 'date') return setDate(fieldValue)
        if (type === 'datetime-local') return setDateTime(fieldValue)
        if (multiLanguage && typeof fieldValue === 'object') {
            return fieldValue[userLanguage] || '';
        }
        return fieldValue;
    }, [fieldValue, type, userLanguage, multiLanguage])

    function onChangeField(e) {
        const {target: {value}} = e;
        const parsedValue = value ?
            type === 'integer' ? parseInt(value) :
                type === 'number' ? Number(value) :
                    type === 'datetime-local' ? value :
                        value : '';
        if (multiLanguage) {
            let isObjectValue = typeof fieldValue === 'object';
            let lngObject = {
                [userLanguage]: parsedValue
            };
            const readyValue = isObjectValue ? {...fieldValue, ...lngObject} : lngObject;
            onChange({
                target: {
                    value: readyValue
                }
            })
            return;
        }
        onChange({target: {value: parsedValue}})
    }

    function onKeyDownField(e) {
        if (valueDropdownOnly && e.key !== "Tab") {
            e.preventDefault();
        }

        if (type === 'integer') {
            let invalidChars = [
                "-",
                "+",
                "e",
                "."
            ];
            if (invalidChars.includes(e.key)) {
                e.preventDefault();
            }
        }
        if (onKeyDown) {
            onKeyDown(e)
        }

        const onKeydownEvent = events?.find(v => v.event === 'onKeydown')

        if (onKeydownEvent) {
            shortcutCombinationMatcher(e, onKeydownEvent.shortcut, () => {
                doActions(onKeydownEvent.actions, {currentField: {value: fieldValue}})
            })
        }
    }

    useEventListener('keydown', (e) => {
        if (disableKeydown) e.preventDefault()
    }, inputRef.current)

    function applyMultiLngMenu(values) {
        onChange({
            target: {
                value: values
            }
        })
        setTimeout(() => {
            onBlur();
        }, 100)
    }

    return (
        <TextField
            name={path}
            innerRef={inputRef}
            fullWidth={true}
            size={"small"}
            label={(isMobile || fieldStyles.presentation === 'standard') && multiLanguageValue(label, userLanguage)}
            placeholder={multiLanguageValue(placeholder, userLanguage)}
            variant={isMobile ? 'standard' : fieldPresentation}
            error={error.length > 0}
            inputRef={fieldRef}
            autoFocus={autofocus}
            onChange={onChangeField}
            onClick={e => {
                e.stopPropagation()

            }}
            onBlur={onBlur}
            type={type === 'integer' ? 'number' : type}
            disabled={disabled}
            required={required}
            InputLabelProps={{shrink: (type === 'date' || type === 'datetime-local') ? true : undefined}}
            className={clsx(inputClassName, "sb-field", screenName)}
            classes={{root: classes.textFieldRoot}}
            autoComplete={autocomplete ? autocomplete : 'off'}
            id={autocomplete ? autocomplete : field.id}
            style={customStyles}
            value={currentValue}
            onKeyDown={onKeyDownField}
            InputProps={{
                endAdornment: <>{
                    multiLanguage && <MultiLngMenu value={fieldValue} apply={applyMultiLngMenu}
                                                   disabled={disabled}
                    />}
                    {dropdown && (
                        <ArrowDropDownIcon
                            onClick={(e) => {
                                e.stopPropagation();

                                if (isOpened) {
                                    closeDropdown();
                                    return
                                }
                                openDropdown()
                            }}
                            style={{
                                // marginLeft: -23,
                                transform: isOpened ? "rotate(180deg)" : "rotate(0deg)",
                                position: 'absolute',
                                cursor: 'pointer',
                                right: ((type === 'date' || type === 'datetime-local') && !valueDropdownOnly) ? 20 : 9,
                                color: disabled ? 'rgba(0, 0, 0, 0.38)' : 'rgba(0, 0, 0, 0.54)',
                            }}/>)}
                    {endAdornment && (
                        <>
                            <IconCustom iconType={'outlined'} style={{fontSize: 16}}>
                                {endAdornment}
                            </IconCustom>
                        </>
                    )}
                </>,
                startAdornment: startAdornment ? <>
                    {startAdornment && <IconCustom>
                        {startAdornment}
                    </IconCustom>}
                </> : null
            }
            }
            inputProps={{
                id: field.id,
                style: inputProps.style,
                inputMode: type === "integer" ? "numeric" : type === "number" ? "decimal" : "text",
                pattern,
                onKeyDown: () => false,
                title: multiLanguageValue(patternTitle, userLanguage),
                minLength: min ? min : 1,
                maxLength: max ? max : 255,
                min: min,
                max: max,
                step: type === 'integer' ? 1 : decimalPlaces ?
                    setDecimalPlaces(decimalPlaces) : type === 'time' ? 1
                        : 0.001
            }}
        />
    )
};
export default SimpleField;


SimpleField.propTypes = {
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    isMobile: PropTypes.bool,
    fieldStyles: PropTypes.object,
    fieldValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    error: PropTypes.string,
    fieldRef: PropTypes.any,
    autofocus: PropTypes.bool,
    type: PropTypes.string,
    disabled: PropTypes.bool,
    required: PropTypes.bool,
    minLength: PropTypes.string,
    maxLength: PropTypes.string,
    min: PropTypes.number,
    max: PropTypes.number,
    decimalPlaces: PropTypes.string,
    pattern: PropTypes.string,
    fieldPresentation: PropTypes.string,
    valueDropdownOnly: PropTypes.bool
};
SimpleField.defaultProps = {
    fieldPresentation: 'outlined'
}


function setDecimalPlaces(decimalPlaces) {
    let step = 0.1;
    let powered = Math.pow(10, decimalPlaces > 6 ? 5 : decimalPlaces - 1);
    return step / powered;
}

function setDateTime(value = '') {
    const user = JSON.parse(sessionStorage.getItem('user')) || {};
    const {
        system: {
            useTimeZone = 'UTC'
        } = {}
    } = user;
    if (!value) return '';
    const now = new Date(value);
    if ((now !== "Invalid Date") && !isNaN(now)) {
        // const utcString = now.toISOString().substring(0, 19);
        // const year = now.getFullYear();
        // const month = now.getMonth() + 1;
        // const day = now.getDate();
        // const hour = now.getHours();
        // const minute = now.getMinutes();
        // const second = now.getSeconds();
        // const milliSec = now.getMilliseconds();
        // const v = year + "-" +
        //     (month < 10 ? "0" + month.toString() : month) + "-" +
        //     (day < 10 ? "0" + day.toString() : day) + "T" +
        //     (hour < 10 ? "0" + hour.toString() : hour) + ":" +
        //     (minute < 10 ? "0" + minute.toString() : minute) +
        //     utcString.substring(16, 19) + '.' + milliSec;
        const isIso = isIsoDate(value);

        return isIso ? moment(value).utcOffset(useTimeZone).format('YYYY-MM-DDTHH:mm:ss.SSS') :
            moment(value).format('YYYY-MM-DDTHH:mm:ss.SSS')
    }
    return '';
}

function setDate(value = "") {
    const formattedDate = formatValue(new Date(value), 'date', 'YYYY-MM-DD');
    if (formattedDate instanceof Date) return formattedDate;
    return value;
}

function isIsoDate(str) {
    if (!/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/.test(str)) return false;
    const d = new Date(str);
    return d instanceof Date && !isNaN(d.getTime()) && d.toISOString() === str; // valid date
}